import React, { useEffect, useMemo, useState } from 'react'

import { SimpleProductDetailsFragment } from '../../graphql/magento'
import { ProductLabelStatus, SearchParentProductBySkuDocument } from '../../graphql/search'
import { useLabelName } from '../../hooks/useLabelName'
import { setAnalyticsEvent } from '../../lib/analytics/state'
import { useDispensaryContext } from '../../lib/dispensaries'
import {
  closeQuickViewProductModal,
  QuickViewProductModalData,
  useQuickViewProductModalState,
} from '../../lib/products/quick-view-modal'
import { useSearchLazyQuery } from '../../lib/search'
import ItemControlsContainer from './details/ItemControlsContainer'
import { ProductModal, ProductModalContent } from './ProductModal'

const QuickViewProductModalContent: React.FC<{
  onCloseModal: () => void
  modalData: QuickViewProductModalData
}> = ({ modalData }) => {
  const { item, defaultChildSku, analyticsEventAppContext, controls } = modalData

  const [selectedVariantSku, setSelectedVariantSku] = useState<string>(defaultChildSku)

  const selectedVariant = useMemo<SimpleProductDetailsFragment | null>(
    () => item?.variants?.find((ver) => ver?.product?.sku === selectedVariantSku)?.product ?? null,
    [item, selectedVariantSku],
  )
  const dispensary = useDispensaryContext()
  const labelNameResult = useLabelName()
  const [executeParentProductBySkuSearch, { data: lazyData, called: lazyCalled }] =
    useSearchLazyQuery(SearchParentProductBySkuDocument, {
      notifyOnNetworkStatusChange: true,
    })

  const selectedVariantLabelStatus = useMemo(
    () =>
      selectedVariant?.sku
        ? (lazyData?.parentProductBySku?.products.edges.find(
            ({ node }) => node && node.sku === selectedVariant.sku,
          )?.node.labelStatus as ProductLabelStatus)
        : undefined,
    [selectedVariant?.sku, lazyData?.parentProductBySku?.products.edges],
  )

  const selectedVariantRetailPrice = useMemo(
    () =>
      selectedVariant?.sku
        ? lazyData?.parentProductBySku?.products.edges.find(
            ({ node }) => node && node.sku === selectedVariant.sku,
          )?.node.retailPrice
        : undefined,
    [selectedVariant?.sku, lazyData?.parentProductBySku?.products.edges],
  )

  // Conditionally search parent product by sku for label statuses
  useEffect(() => {
    const exactVariantSkus =
      item.variants?.reduce((acc: string[], variant) => {
        const variantProductSku = variant?.product?.sku
        if (variantProductSku) {
          acc.push(variantProductSku)
        }
        return acc
      }, []) || []

    // Verify there are variants to check label statuses
    if (
      labelNameResult._tag === 'Success' &&
      item.sku &&
      exactVariantSkus.length > 0 &&
      !lazyCalled
    ) {
      executeParentProductBySkuSearch({
        variables: {
          sku: item.sku,
          labelName: labelNameResult.labelName,
          includeLabel: true,
        },
      })
    } else if (
      labelNameResult._tag !== 'Success' &&
      item.sku &&
      exactVariantSkus.length > 0 &&
      !lazyCalled
    ) {
      // request parent product since we need retail price for each child product
      executeParentProductBySkuSearch({
        variables: {
          sku: item.sku,
          labelName: '',
          includeLabel: false,
        },
      })
    }
  }, [executeParentProductBySkuSearch, item, labelNameResult, lazyCalled])

  useEffect(() => {
    if (!selectedVariant || selectedVariant.__typename !== 'SimpleProduct') {
      return
    }
    const finalPrice = selectedVariant.price_tiers?.[0]?.final_price
    const currency = finalPrice?.currency
    const price = finalPrice?.value
    if (currency == null || price == null) {
      return
    }
    setAnalyticsEvent({
      event: 'view_item',
      currency,
      value: price,
      items: [
        {
          item_id: selectedVariant.sku || undefined,
          item_name: selectedVariant.name as string,
          currency,
          price,
        },
      ],
      app_context: dispensary ? 'dispensary_modal' : 'modal',
    })
  }, [selectedVariant, dispensary])

  return (
    <ProductModalContent
      onCloseModal={closeQuickViewProductModal}
      modalData={modalData}
      selectedVariant={selectedVariant}
      variantRetailPrice={selectedVariantRetailPrice}
    >
      <ItemControlsContainer
        controls={controls}
        item={item}
        onVariantSelected={setSelectedVariantSku}
        selectedVariant={selectedVariant}
        variantLabelStatus={selectedVariantLabelStatus}
        analyticsEventAppContext={analyticsEventAppContext}
      />
    </ProductModalContent>
  )
}

export const QuickViewProductModal: React.VFC = () => {
  const state = useQuickViewProductModalState()
  if (state.type === 'CLOSED') {
    return null
  }
  return (
    <ProductModal state={state} onCloseModal={closeQuickViewProductModal}>
      {state.type === 'OPEN' && (
        <QuickViewProductModalContent
          onCloseModal={closeQuickViewProductModal}
          modalData={state.data}
        />
      )}
    </ProductModal>
  )
}
